import React, { useState, useEffect } from 'react';
import './StrokeAnalysis.css';

const StrokeAnalysis = ({ analysisData, jobId }) => {
  const [strokeData, setStrokeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  useEffect(() => {
    if (analysisData) {
      setLoading(false);
      setStrokeData(analysisData);
    }
  }, [analysisData]);

  const getStrokeColor = (stroke) => {
    const colors = {
      'freestyle': '#10B981', // Green
      'backstroke': '#3B82F6', // Blue
      'breaststroke': '#EF4444', // Red
      'butterfly': '#F59E0B', // Yellow
      'unknown': '#6B7280' // Gray
    };
    return colors[stroke] || colors['unknown'];
  };

  const getStrokeIcon = (stroke) => {
    const icons = {
      'freestyle': '🏊‍♂️',
      'backstroke': '🏊‍♀️',
      'breaststroke': '🐸',
      'butterfly': '🦋',
      'unknown': '❓'
    };
    return icons[stroke] || icons['unknown'];
  };

  const getStrokeDescription = (stroke) => {
    const descriptions = {
      'freestyle': 'Alternating arm movements with body roll and side breathing',
      'backstroke': 'Face-up position with alternating arms and minimal body roll',
      'breaststroke': 'Simultaneous arm pull with frog kick and head bobbing',
      'butterfly': 'Simultaneous arm recovery with dolphin kick and undulating motion',
      'unknown': 'Stroke pattern not clearly identified'
    };
    return descriptions[stroke] || descriptions['unknown'];
  };

  const formatConfidence = (confidence) => {
    return Math.round(confidence * 100);
  };

  if (loading) {
    return (
      <div className="stroke-analysis-loading">
        <div className="loading-spinner"></div>
        <p>Loading stroke analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stroke-analysis-error">
        <span className="error-icon">❌</span>
        <p>Error loading stroke analysis: {error}</p>
      </div>
    );
  }

  if (!strokeData) {
    return (
      <div className="stroke-analysis-no-data">
        <span className="no-data-icon">📊</span>
        <p>No stroke analysis data available</p>
      </div>
    );
  }

  // Extract stroke classification summary
  const strokeSummary = strokeData.average_metrics?.stroke_classification_summary;
  const detailedData = strokeData.detailed_data || [];

  // Calculate stroke distribution
  const strokeDistribution = {};
  let totalFrames = 0;

  detailedData.forEach(frame => {
    if (frame.analysis?.stroke_classification?.primary_stroke) {
      const stroke = frame.analysis.stroke_classification.primary_stroke;
      strokeDistribution[stroke] = (strokeDistribution[stroke] || 0) + 1;
      totalFrames++;
    }
  });

  // Calculate percentages
  const strokePercentages = {};
  Object.keys(strokeDistribution).forEach(stroke => {
    strokePercentages[stroke] = (strokeDistribution[stroke] / totalFrames) * 100;
  });

  // Get dominant stroke
  const dominantStroke = strokeSummary?.dominant_stroke || 
    Object.keys(strokePercentages).reduce((a, b) => 
      strokePercentages[a] > strokePercentages[b] ? a : b, 'unknown'
    );

  return (
    <div className="stroke-analysis">
      <div className="stroke-analysis-header">
        <h3 className="stroke-analysis-title">
          <span className="title-icon">🏊‍♂️</span>
          Stroke Analysis
        </h3>
        <p className="stroke-analysis-subtitle">
          AI-powered stroke classification and technique analysis
        </p>
      </div>

      {/* Dominant Stroke Display */}
      <div className="dominant-stroke-card">
        <div className="dominant-stroke-header">
          <div className="stroke-icon-large">
            {getStrokeIcon(dominantStroke)}
          </div>
          <div className="stroke-info">
            <h4 className="stroke-name">
              {dominantStroke.charAt(0).toUpperCase() + dominantStroke.slice(1)}
            </h4>
            <p className="stroke-description">
              {getStrokeDescription(dominantStroke)}
            </p>
          </div>
        </div>
        
        <div className="stroke-confidence">
          <div className="confidence-bar">
            <div 
              className="confidence-fill"
              style={{ 
                width: `${strokeSummary?.classification_confidence || 0}%`,
                backgroundColor: getStrokeColor(dominantStroke)
              }}
            ></div>
          </div>
          <span className="confidence-text">
            {strokeSummary?.classification_confidence?.toFixed(1) || '0.0'}% Confidence
          </span>
        </div>
      </div>

      {/* Stroke Distribution */}
      <div className="stroke-distribution">
        <h4 className="distribution-title">Stroke Distribution</h4>
        <div className="distribution-grid">
          {Object.entries(strokePercentages)
            .sort(([,a], [,b]) => b - a)
            .map(([stroke, percentage]) => (
            <div key={stroke} className="distribution-item">
              <div className="distribution-header">
                <span className="distribution-icon">{getStrokeIcon(stroke)}</span>
                <span className="distribution-name">
                  {stroke.charAt(0).toUpperCase() + stroke.slice(1)}
                </span>
              </div>
              <div className="distribution-bar">
                <div 
                  className="distribution-fill"
                  style={{ 
                    width: `${percentage}%`,
                    backgroundColor: getStrokeColor(stroke)
                  }}
                ></div>
              </div>
              <div className="distribution-stats">
                <span className="percentage">{percentage.toFixed(1)}%</span>
                <span className="count">({strokeDistribution[stroke]} frames)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stroke Features Analysis */}
      {detailedData.length > 0 && (
        <div className="stroke-features">
          <h4 className="features-title">Key Stroke Features</h4>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <div className="feature-content">
                <h5>Arm Coordination</h5>
                <p>Analysis of arm movement patterns and synchronization</p>
              </div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌊</div>
              <div className="feature-content">
                <h5>Body Position</h5>
                <p>Body roll, streamline, and orientation analysis</p>
              </div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🦵</div>
              <div className="feature-content">
                <h5>Kick Pattern</h5>
                <p>Leg movement analysis and kick type identification</p>
              </div>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💨</div>
              <div className="feature-content">
                <h5>Breathing</h5>
                <p>Head movement and breathing pattern analysis</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Summary */}
      <div className="analysis-summary">
        <h4 className="summary-title">Analysis Summary</h4>
        <div className="summary-stats">
          <div className="summary-stat">
            <span className="stat-label">Total Frames Analyzed</span>
            <span className="stat-value">{totalFrames}</span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Detection Rate</span>
            <span className="stat-value">
              {strokeData.performance_stats?.pose_detection_rate?.toFixed(1) || '0.0'}%
            </span>
          </div>
          <div className="summary-stat">
            <span className="stat-label">Processing Time</span>
            <span className="stat-value">
              {strokeData.performance_stats?.avg_frame_time ? 
                `${(strokeData.performance_stats.avg_frame_time * 1000).toFixed(1)}ms/frame` : 
                'N/A'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Real-time Stroke Timeline */}
      {detailedData.length > 0 && (
        <div className="stroke-timeline">
          <h4 className="timeline-title">Stroke Timeline</h4>
          <div className="timeline-container">
            {detailedData.slice(0, 20).map((frame, index) => {
              const stroke = frame.analysis?.stroke_classification?.primary_stroke || 'unknown';
              const confidence = frame.analysis?.stroke_classification?.confidence || 0;
              
              return (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker" style={{ backgroundColor: getStrokeColor(stroke) }}>
                    {getStrokeIcon(stroke)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-stroke">
                      {stroke.charAt(0).toUpperCase() + stroke.slice(1)}
                    </div>
                    <div className="timeline-confidence">
                      {formatConfidence(confidence)}% confidence
                    </div>
                    <div className="timeline-time">
                      {frame.timestamp?.toFixed(1)}s
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {detailedData.length > 20 && (
            <div className="timeline-more">
              <span>... and {detailedData.length - 20} more frames</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StrokeAnalysis;
